import Button from '@risksmart-app/components/src/button';
import {
  useGetGuidParam,
  useGetOptionalGuidParam,
} from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import {
  useGetInternalAuditReportById,
  useGetInternalAuditResultsById,
} from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteInternalAuditResults } from '@/hooks/mutations/internal-audit-result/useDeleteInternalAuditResults';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { internalAuditReportResultsUrl } from '@/utils/urls';

import { useTabs } from '../useTabs';

const Page: FC = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'assessmentResults' });
  const title = t('edit_title');
  const optionalInternalAuditReportId = useGetOptionalGuidParam('assessmentId');
  const findingId = useGetGuidParam('findingId');
  const navigate = useNavigate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const { data: resultData, error: resultError } =
    useGetInternalAuditResultsById({
      queryArgs: { internalAuditResultId: findingId },
    });

  if (resultError) {
    throw resultError;
  }
  const resultParentId = resultData?.internal_audit_result_parent.find(
    (ar) => ar.ParentType == Parent_Type_Enum.InternalAuditReport
  )?.ParentId;
  const internalAuditReportId = optionalInternalAuditReportId ?? resultParentId;

  const { data, error } = useGetInternalAuditReportById({
    queryArgs: { reportId: internalAuditReportId ?? '' },
    shouldSkip: !internalAuditReportId,
  });

  if (error) {
    throw error;
  }

  const internalAuditReport = data?.internal_audit_report?.[0];

  const {
    hasPermission: canDeleteDocumentInternalAuditResult,
    loading: canDeleteDocumentLoading,
  } = useHasPermissionQuery(
    'delete:document_internal_audit_result',
    internalAuditReport
  );
  const {
    hasPermission: canDeleteObligationInternalAuditResult,
    loading: canDeleteObligationLoading,
  } = useHasPermissionQuery(
    'delete:obligation_internal_audit_result',
    internalAuditReport
  );
  const {
    hasPermission: canDeleteRiskControlledInternalAuditResult,
    loading: canDeleteRiskControlledLoading,
  } = useHasPermissionQuery(
    'delete:risk_controlled_internal_audit_result',
    internalAuditReport
  );
  const {
    hasPermission: canDeleteRiskUncontrolledInternalAuditResult,
    loading: canDeleteRiskUncontrolledLoading,
  } = useHasPermissionQuery(
    'delete:risk_uncontrolled_internal_audit_result',
    internalAuditReport
  );
  const {
    hasPermission: canDeleteControlTestInternalAuditResult,
    loading: canDeleteControlTestLoading,
  } = useHasPermissionQuery(
    'delete:control_test_internal_audit_result',
    internalAuditReport
  );

  const isPermissionLoading =
    canDeleteDocumentLoading ||
    canDeleteObligationLoading ||
    canDeleteRiskControlledLoading ||
    canDeleteRiskUncontrolledLoading ||
    canDeleteControlTestLoading;

  const canDelete =
    !isPermissionLoading &&
    (canDeleteDocumentInternalAuditResult ||
      canDeleteObligationInternalAuditResult ||
      canDeleteRiskControlledInternalAuditResult ||
      canDeleteRiskUncontrolledInternalAuditResult ||
      canDeleteControlTestInternalAuditResult);

  // The wrapper hook owns the eviction/invalidation set for both branches
  // (internalAuditResult + internalAuditReport families), which covers the
  // refetchQueries this page used to declare.
  const { deleteInternalAuditResults, loading: deleteLoading } =
    useDeleteInternalAuditResults();

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      await deleteInternalAuditResults([findingId]);
      setIsDeleteModalVisible(false);
      navigate(internalAuditReportResultsUrl(internalAuditReportId));

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const tabs = useTabs(
    internalAuditReport || undefined,
    'update',
    !optionalInternalAuditReportId,
    !!optionalInternalAuditReportId
  );

  // Object-level toolbar (CUS-165) — flag-gated. The result-edit surface's only
  // header action is Delete, relocated to the toolbar's danger (⋮) group. The
  // delete gate is a build-time boolean (an OR across five result-type
  // `delete:*` permissions), so the danger action is included conditionally
  // rather than via the toolbar's per-action `permission` gate.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      actions: canDelete
        ? {
            danger: [
              {
                id: 'delete',
                label: t('delete_button'),
                iconName: 'trash-01',
                // Gates on the parent id, not the entity: the post-delete
                // `navigate` needs it, so an early click would delete and then
                // navigate to `undefined` (ENG-616 class).
                disabled: !internalAuditReportId,
                onClick: () => setIsDeleteModalVisible(true),
              },
            ],
          }
        : undefined,
    }),
    [title, canDelete, internalAuditReportId, t]
  );

  return (
    <PageLayout
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        canDelete ? (
          <Button
            formAction={'none'}
            variant={'normal'}
            onClick={() => setIsDeleteModalVisible(true)}
          >
            {t('delete_button')}
          </Button>
        ) : undefined
      }
    >
      <ControlledTabs
        tabs={tabs}
        variant={'container'}
        parent={internalAuditReport}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        loading={deleteLoading}
        header={t('delete_button')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('confirm_single_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
